// src/pages/Convocations/components/ConvocationsHero.jsx

import React from "react";

import {
  FaBell,
  FaCalendarCheck,
  FaSyncAlt,
  FaUserTie,
} from "react-icons/fa";

import {
  cx,
  getActorLabel,
} from "../utils/convocation.utils";

export default function ConvocationsHero({
  actors,
  pendingCount,
  refreshing,
  onRefresh,
  onShowPending,
}) {
  const safeActors = Array.isArray(
    actors,
  )
    ? actors
    : [];

  return (
    <section
      className="
        relative overflow-hidden
        rounded-[28px]
        bg-gradient-to-br
        from-[#003b70]
        via-[#005495]
        to-[#0877bd]
        px-5 py-6
        text-white
        shadow-[0_30px_80px_-40px_rgba(0,59,112,0.8)]
        sm:px-7 sm:py-8
        lg:px-10 lg:py-10
      "
    >
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute -right-20 -top-24
          h-64 w-64
          rounded-full
          bg-white/10
          blur-3xl
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute -bottom-28 left-1/3
          h-60 w-60
          rounded-full
          bg-orange-400/20
          blur-3xl
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute right-8 top-1/2
          hidden -translate-y-1/2
          text-[190px]
          text-white/[0.045]
          lg:block
        "
      >
        <FaCalendarCheck />
      </div>

      <div className="relative z-10">
        <div
          className="
            flex flex-col gap-6
            lg:flex-row
            lg:items-center
            lg:justify-between
          "
        >
          <div className="max-w-3xl">
            <div
              className="
                inline-flex items-center gap-2
                rounded-full
                border border-white/20
                bg-white/10
                px-3 py-1.5
                text-[11px] font-black
                uppercase
                tracking-[0.16em]
                backdrop-blur
              "
            >
              <FaCalendarCheck />

              Espace acteur
            </div>

            <h1
              className="
                mt-4
                font-satoshi
                text-3xl font-black
                tracking-tight
                sm:text-4xl
                lg:text-5xl
              "
            >
              Mes convocations
            </h1>

            <p
              className="
                mt-3 max-w-2xl
                text-sm font-medium
                leading-6
                text-blue-100
                sm:text-base
                sm:leading-7
              "
            >
              Consultez vos affectations,
              les informations des
              compétitions et répondez
              rapidement aux responsables
              de la Ligue.
            </p>

            {safeActors.length > 0 ? (
              <div className="mt-5">
                <p
                  className="
                    mb-2
                    inline-flex items-center
                    gap-2
                    text-xs font-black
                    uppercase
                    tracking-[0.12em]
                    text-blue-100
                  "
                >
                  <FaUserTie />

                  {safeActors.length > 1
                    ? "Profils associés"
                    : "Profil associé"}
                </p>

                <div className="flex flex-wrap gap-2">
                  {safeActors.map(
                    (actor) => (
                      <span
                        key={actor.id}
                        className="
                          inline-flex items-center
                          rounded-full
                          border border-white/15
                          bg-white/10
                          px-3 py-1.5
                          text-xs font-bold
                          text-white
                          backdrop-blur
                        "
                      >
                        {getActorLabel(
                          actor,
                        )}

                        {actor.club_acteur
                          ? ` · ${actor.club_acteur}`
                          : ""}
                      </span>
                    ),
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div
            className="
              flex w-full flex-col gap-3
              sm:flex-row
              lg:w-auto
              lg:min-w-[280px]
              lg:flex-col
            "
          >
            <button
              type="button"
              onClick={onShowPending}
              disabled={
                pendingCount <= 0
              }
              className={cx(
                [
                  "flex min-h-[58px]",
                  "flex-1 items-center",
                  "justify-between gap-4",
                  "rounded-2xl",
                  "border border-white/20",
                  "bg-white/10",
                  "px-4 py-3",
                  "text-left",
                  "backdrop-blur",
                  "transition",
                  "hover:bg-white/15",
                  "disabled:cursor-default",
                  "disabled:opacity-70",
                ].join(" "),
              )}
            >
              <span>
                <span
                  className="
                    block text-[10px]
                    font-black uppercase
                    tracking-[0.14em]
                    text-blue-100
                  "
                >
                  Réponses attendues
                </span>

                <span
                  className="
                    mt-0.5 block
                    text-sm font-black
                    text-white
                  "
                >
                  Voir les convocations
                </span>
              </span>

              <span
                className="
                  relative flex h-11 w-11
                  shrink-0 items-center
                  justify-center
                  rounded-2xl
                  bg-amber-400
                  text-lg font-black
                  text-slate-950
                  shadow-lg
                  shadow-amber-950/20
                "
              >
                <FaBell
                  className={
                    pendingCount > 0
                      ? "animate-pulse"
                      : ""
                  }
                />

                {pendingCount > 0 ? (
                  <span
                    className="
                      absolute -right-2 -top-2
                      inline-flex min-h-6
                      min-w-6 items-center
                      justify-center
                      rounded-full
                      bg-rose-600
                      px-1
                      text-[10px] font-black
                      text-white
                      ring-2 ring-[#075d9d]
                    "
                  >
                    {pendingCount > 99
                      ? "99+"
                      : pendingCount}
                  </span>
                ) : null}
              </span>
            </button>

            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="
                inline-flex min-h-[58px]
                items-center justify-center
                gap-2 rounded-2xl
                border border-white/20
                bg-white
                px-5
                text-sm font-black
                text-[#004b86]
                shadow-xl
                transition
                hover:bg-blue-50
                active:scale-[0.98]
                disabled:cursor-wait
                disabled:opacity-70
              "
            >
              <FaSyncAlt
                className={
                  refreshing
                    ? "animate-spin"
                    : ""
                }
              />

              {refreshing
                ? "Actualisation..."
                : "Actualiser"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}